import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, BookOpen, ArrowRight } from 'lucide-react';
import { BackLink } from '../components/ui/BackLink';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { TUTORIALS, TUTORIAL_SEGMENTS } from '../content/tutorials';
import { getAuthor } from '../content/authors';
import { tutorialsIndexJsonLd } from '../content/tutorialJsonLd';

/**
 * Public tutorials index — /tutorials. Crawlable (no auth), real content for SEO/AEO/GEO. A search
 * box + segment filters over the committed tutorials; each links to its own page and author.
 */
const TutorialsIndex: React.FC = () => {
  const [q, setQ] = useState('');
  const [segment, setSegment] = useState<string>('All');

  useDocumentMeta({
    title: 'Tutorials — Wisetree AI',
    description: 'Practical, task-focused tutorials for getting more out of Wisetree AI — building a team of specialists, making media to share, researching a market, and turning ideas into decks.',
    canonical: 'https://wisetree.ai/tutorials',
    jsonLd: tutorialsIndexJsonLd(TUTORIALS),
  });

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return TUTORIALS.filter((t) => {
      if (segment !== 'All' && t.segment !== segment) return false;
      if (!needle) return true;
      const hay = `${t.title} ${t.description} ${t.tldr} ${t.useCase} ${t.segment} ${t.keyTerms.map((k) => k.term).join(' ')}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [q, segment]);

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <BackLink to="/" label="Wisetree AI" />

        <div className="mt-6 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-secondary-700 dark:text-secondary-200" />
          <h1 className="text-2xl font-bold">Tutorials</h1>
        </div>
        <p className="mt-1 text-sm text-secondary-500">Task-focused walkthroughs for getting the most out of your team — written by our AI writing team.</p>

        {/* Search */}
        <div className="relative mt-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)} type="search" placeholder="Search tutorials…"
            data-testid="tutorial-search"
            className="w-full rounded-xl border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-900 pl-9 pr-3 py-2.5 text-sm focus:border-secondary-400 focus:outline-none"
          />
        </div>

        {/* Segment filters */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {['All', ...TUTORIAL_SEGMENTS].map((s) => (
            <button key={s} onClick={() => setSegment(s)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${segment === s ? 'bg-secondary-900 text-white dark:bg-white dark:text-secondary-900' : 'text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800 border border-secondary-200 dark:border-secondary-700'}`}>
              {s}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3" data-testid="tutorial-list">
          {results.length === 0 ? (
            <p className="text-sm text-secondary-400">No tutorials match “{q}”.</p>
          ) : results.map((t) => {
            const author = getAuthor(t.authorId);
            return (
              <Link key={t.slug} to={`/tutorials/${t.slug}`}
                className="group block rounded-xl border border-secondary-200 dark:border-secondary-800 p-5 hover:border-secondary-400 dark:hover:border-secondary-600 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-secondary-400">{t.segment} · {t.useCase}</div>
                    <h2 className="mt-1 text-base font-semibold text-secondary-900 dark:text-white">{t.title}</h2>
                    <p className="mt-1 text-sm text-secondary-500 leading-snug">{t.description}</p>
                    {author && <p className="mt-2 text-xs text-secondary-400">{author.name} · {t.readingTimeMin} min read</p>}
                  </div>
                  <ArrowRight className="w-4 h-4 shrink-0 mt-1 text-secondary-300 group-hover:text-secondary-600 dark:group-hover:text-secondary-300 transition-colors" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TutorialsIndex;
